import React from "react";
import { Box, Typography } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  ContactChannel,
  ContactPhone,
  WHATSAPP_BASE_URL,
  WHATSAPP_GREEN,
  WHATSAPP_GREEN_LIGHT,
  CARD_BORDER_COLOR,
} from "../../constants/contactChannels.constants";

interface ContactCardProps {
  channel: ContactChannel;
}

const ContactCard: React.FC<ContactCardProps> = ({ channel }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {channel.phones.map((phone) => {
        const hasValidNumber = Boolean(
          phone.number && phone.number.trim() !== ""
        );

        return (
          <SinglePhoneCard
            key={`${channel.id}-${phone.number}`}
            channel={channel}
            phone={phone}
            hasValidNumber={hasValidNumber}
          />
        );
      })}
    </Box>
  );
};

interface SinglePhoneCardProps {
  channel: ContactChannel;
  phone: ContactPhone;
  hasValidNumber: boolean;
}

const SinglePhoneCard: React.FC<SinglePhoneCardProps> = ({
  channel,
  phone,
  hasValidNumber,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "space-between",
        padding: { xs: "12px", sm: "16px 20px" },
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        border: `1px solid ${CARD_BORDER_COLOR}`,
        gap: 1.5,
        "@media (max-width: 400px)": {
          flexWrap: "wrap",
        },
      }}
    >
      {/* Left: WhatsApp icon + title/description */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            minWidth: 40,
            borderRadius: "50%",
            backgroundColor: WHATSAPP_GREEN_LIGHT,
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 22, color: WHATSAPP_GREEN }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{
              fontWeight: 700,
              fontSize: "1.4rem",
              color: "text.primary",
              lineHeight: 1.4,
            }}
          >
            {channel.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "1.4rem",
              color: "text.disabled",
              lineHeight: 1.4,
            }}
          >
            {channel.description}
          </Typography>
        </Box>
      </Box>

      {/* Right: phone number + "Abrir WhatsApp" link */}
      {hasValidNumber && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            ml: 0,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.4rem",
              color: "text.primary",
              whiteSpace: "nowrap",
            }}
          >
            {phone.displayNumber}
          </Typography>

          <Box
            component="a"
            href={`${WHATSAPP_BASE_URL}${phone.number}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Abrir WhatsApp para ${phone.displayNumber}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              color: WHATSAPP_GREEN,
              fontWeight: 600,
              fontSize: "1.4rem",
              textDecoration: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Abrir WhatsApp
            <ChevronRightIcon sx={{ fontSize: 18, ml: 0.25 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ContactCard;
